import React, {useState} from "react";
import {GridColDef} from "@mui/x-data-grid";
import {PersistentStateId} from "../../../../store/common/Grid";
import StereotypeCreateDialog from "./StereotypeCreateDialog";
import {ActionButtonType, EnabledPolicy, GridAction} from "../../../../components/grid/GridAction";
import ConfirmationDialog from "../../../../components/dialogs/ConfirmationDialog";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SucceededAlert from "../../../../components/SucceededAlert";
import NotSucceededAlert from "../../../../components/NotSucceededAlert";
import Constants from "../../../../common/Constants";
import {_transl} from "../../../../store/localization/TranslMessasge";
import {StereotypesTranslationKey} from "./StereotypesTranslationKey";
import ExtGridWrapper from "../../../../components/grid/ExtGridWrapper";
import {ArchimateElement} from "../../../../common/archimate/ArchimateElement";
import stereotypeService from "../../../../common/apis/stereotype/StereotypeApiClient";
import {CommonTranslation} from "../CommonTranslation";
import {ErrorTranslationKey} from "../ErrorTranslationKey";
import {StereotypeDetailDialog} from "./StereotypeDetailDialog";
import {useFindAllStereotypesQuery} from "./StereotypeQuery";


function createColumnDef(): GridColDef[] {
    return [
        {field: 'id', headerName: _transl(StereotypesTranslationKey.IDENTIFIER), width: 200},
        {field: 'name', headerName: _transl(StereotypesTranslationKey.NAME), flex: 2, minWidth: 150},
        {field: 'type', headerName: _transl(StereotypesTranslationKey.BASE_TYPE), flex: 1, minWidth: 100},
        {field: 'description', headerName: _transl(CommonTranslation.DESCRIPTION), flex: 3, minWidth: 200},
    ];
}

enum StereotypeActionButton {
    ADD = "StereotypeAction.ADD",
    DELETE = "StereotypeAction.DELETE",
}

export default function StereotypesGrid() {

    const [selectedStereotypeId, setSelectedStereotypeId] = useState<string>();

    const [detailDialogOpen, setDetailDialogOpen] = useState<boolean>(false);

    const [stereotypesAddDialogIsOpen, setStereotypesAddDialogIsOpen] = useState<boolean>(false);

    const [isDeleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [isDeleteSuccessfulAlertVisible, setDeleteSuccessfulAlertVisible] = useState<boolean>(false);
    const [isDeleteFailedAlertVisible, setDeleteFailedAlertVisible] = useState<boolean>(false);

    const {data: stereotypeDtos, refetch} = useFindAllStereotypesQuery();

    function openDetailDialog(stereotypeId: string) {
        setDetailDialogOpen(true);
        setSelectedStereotypeId(stereotypeId);
    }

    function closeDetailDialog(updated: boolean) {
        if (updated) {
            refetch();
        }
        setDetailDialogOpen(false);
    }

    function hideAddDialog() {
        setStereotypesAddDialogIsOpen(false)
    }

    function showAddDialog(addLabelsDialogIsOpen: boolean) {
        setStereotypesAddDialogIsOpen(true)
    }

    function openDeleteDialog(selectedRowIds: string[]) {
        if (selectedRowIds.length === 1) {
            setDeleteDialogOpen(true);
            setSelectedStereotypeId(selectedRowIds[0]);
        }
    }

    function closeDeleteDialog() {
        setDeleteDialogOpen(false);
    }

    function onDeleteConfirmed() {
        closeDeleteDialog();
        stereotypeService.deleteStereotypeById(selectedStereotypeId!).subscribe(
            () => onStereotypeDeleted(),
            () => onStereotypeDeleteFailed(),
        );
    }

    function onStereotypeDeleted() {
        refetch();
        showDeleteSuccessfulAlert();
    }

    function showDeleteSuccessfulAlert() {
        setDeleteSuccessfulAlertVisible(true);
        setTimeout(() => setDeleteSuccessfulAlertVisible(false), Constants.FE_APP_ALERT_DELAY);
    }

    function onStereotypeDeleteFailed() {
        showDeleteFailedAlert();
    }

    function showDeleteFailedAlert() {
        setDeleteFailedAlertVisible(true);
        setTimeout(() => setDeleteFailedAlertVisible(false), Constants.FE_APP_ALERT_DELAY);
    }


    const rows = stereotypeDtos?.map((stereotype) => {
        const  element = ArchimateElement.findByStandardName(stereotype.elementType);
        return {
            id: stereotype.id,
            name: stereotype.name,
            type: element?.visibleName ?? stereotype.elementType,
            description: stereotype.description,
        }
    });

    return (
        <>
            <ConfirmationDialog open={isDeleteDialogOpen}
                                title={_transl(StereotypesTranslationKey.DELETE_STEREOTYPE)}
                                confirmationText={_transl(StereotypesTranslationKey.STEREOTYPE_WILL_BE_DELETED)}
                                onConfirm={() => onDeleteConfirmed()}
                                onReject={() => closeDeleteDialog()}/>
            <SucceededAlert text={_transl(StereotypesTranslationKey.STEREOTYPE_DELETED_SUCCESSFULLY)}
                            opened={isDeleteSuccessfulAlertVisible}
                            onClose={() => setDeleteSuccessfulAlertVisible(false)}/>
            <NotSucceededAlert text={_transl(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED)}
                               opened={isDeleteFailedAlertVisible}
                               onClose={() => setDeleteFailedAlertVisible(false)}/>

            <ExtGridWrapper
                columns={createColumnDef()}
                rows={rows ?? []}
                rowCount={rows?.length ?? 0}
                getRowId={row => row.id}
                actions={[
                    GridAction.detailButtonBuilder()
                        .onClick((selectedRowIds) => openDetailDialog(selectedRowIds[0] as string))
                        .build(),
                    GridAction.buttonBuilder(StereotypeActionButton.ADD, ActionButtonType.IMMEDIATE, _transl(StereotypesTranslationKey.ADD_STEREOTYPE),
                        <AddIcon/>)
                        .enabledPolicy(EnabledPolicy.ALWAYS)
                        .onClick(() => showAddDialog(true))
                        .build(),
                    GridAction.buttonBuilder(StereotypeActionButton.DELETE, ActionButtonType.IMMEDIATE, _transl(CommonTranslation.DELETE),
                        <DeleteIcon/>)
                        .onClick(selectedRowIds => openDeleteDialog(selectedRowIds as string[]))
                        .build(),
                ]}
                onRowDoubleClick={(params) => openDetailDialog(params.row.id)}
                peristentStateId={PersistentStateId.STEREOTYPES_PAGE_GRID}
                resourceId={""}
            />

            {selectedStereotypeId &&
                <StereotypeDetailDialog open={detailDialogOpen}
                        stereotypeId={selectedStereotypeId}
                        onClose={(updated) => closeDetailDialog(updated)}/>
            }

            <StereotypeCreateDialog open={stereotypesAddDialogIsOpen}
                    onClosed={() => hideAddDialog()}
                    onStereotypeAdded={() => refetch()}/>
        </>
    );

}
